import type { AuthExchangeRequest, AuthExchangeResponse, ZohoOauthCredentials } from '@kunzite/shared-types';

export type ExchangeFetcher = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: string; credentials?: RequestCredentials },
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

export interface ExchangeDeps {
  baseUrl: string;
  fetch?: ExchangeFetcher;
}

export class AuthExchangeError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'AuthExchangeError';
  }
}

export async function exchangeAuth(
  deps: ExchangeDeps,
  req: AuthExchangeRequest,
): Promise<AuthExchangeResponse> {
  const fetcher: ExchangeFetcher =
    deps.fetch ??
    ((url, init) => fetch(url, init));
  const res = await fetcher(`${deps.baseUrl}/v1/auth/exchange`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
    credentials: 'include',
  });
  const body = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    const code = (body.code as string) ?? `http_${res.status}`;
    const message = (body.message as string) ?? 'auth exchange failed';
    throw new AuthExchangeError(code, message);
  }
  return body as unknown as AuthExchangeResponse;
}

/**
 * Holder for the Zoho OAuth credentials returned by /v1/auth/exchange. The
 * widget keeps these in memory and re-fetches via re-exchange when the token
 * approaches expiration (60s pre-window).
 *
 * F2 closes la deuda crítica de F1: VITE_ZOHO_OAUTH_TOKEN ya no está embebido
 * en el bundle. El backend descifra el refresh_token cifrado y entrega un
 * access_token corto (~1h TTL) por agente en cada exchange.
 *
 * F2 deuda residual: cuando el access_token expira sin un refreshFn registrado
 * (o el refresh falla), getToken() lanza. F3 wires un real refresh path al
 * detectar 401 desde Zoho/IS y re-emite el exchange.
 */
export class ZohoTokenHolder {
  private current: ZohoOauthCredentials | null = null;
  private refreshFn: (() => Promise<ZohoOauthCredentials>) | null = null;

  setInitial(creds: ZohoOauthCredentials, refresh: () => Promise<ZohoOauthCredentials>): void {
    this.current = creds;
    this.refreshFn = refresh;
  }

  async getToken(): Promise<{ accessToken: string; apiDomain: string }> {
    if (!this.current) throw new Error('ZohoTokenHolder not initialized');
    const expiresAtMs = Date.parse(this.current.expiresAt);
    if (Number.isFinite(expiresAtMs) && expiresAtMs - Date.now() < 60_000) {
      if (!this.refreshFn) throw new Error('ZohoTokenHolder: no refresh function registered');
      this.current = await this.refreshFn();
    }
    return { accessToken: this.current.accessToken, apiDomain: this.current.apiDomain };
  }

  setRefresh(fn: () => Promise<ZohoOauthCredentials>): void {
    this.refreshFn = fn;
  }

  /** F3.1: called by CallsApiClient.onJwtUpdate when refresh brings new credentials. */
  updateCreds(creds: ZohoOauthCredentials): void {
    this.current = creds;
  }
}
